import React from 'react'
import styles from './privacy-policy.module.scss'
import UBFLogoBlue from '../../assets/ubf-logo-blue.png'

const PrivacyPolicyPage = () => {
    return (
        <div className={styles._}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <img src={UBFLogoBlue} alt="UBF Nigeria logo" className={styles.logo} />
                    <h1>Privacy Policy</h1>
                    <p className={styles.org}>University Bible Fellowship Nigeria</p>
                    <p className={styles.effective}>Effective Date: March 2026</p>
                </div>

                <hr />

                <section>
                    <h2>1. Overview</h2>
                    <p>
                        This Privacy Policy describes how the UBF Nigeria app ("the App"), developed by
                        University Bible Fellowship Nigeria ("UBF Nigeria", "we", "us"), handles information
                        when you use the App on your mobile device. We are committed to protecting your privacy
                        and handling your data transparently.
                    </p>
                </section>

                <hr />

                <section>
                    <h2>2. Information We Collect</h2>

                    <h3>2.1 Information Stored Locally on Your Device</h3>
                    <p>
                        The App stores the following data exclusively on your device using local storage.
                        This data is never transmitted to our servers:
                    </p>
                    <ul>
                        <li><strong>Favourite Hymns</strong> — hymns you have marked as favourites</li>
                        <li><strong>Recently Viewed Hymns</strong> — a list of up to 5 recently opened hymns</li>
                        <li><strong>Font Size Preference</strong> — your chosen text size setting</li>
                        <li><strong>Theme Preference</strong> — your chosen dark or light mode setting</li>
                        <li><strong>Cached Audio Files</strong> — audio recordings downloaded for offline playback, stored in your device's local cache</li>
                    </ul>

                    <h3>2.2 Information You Voluntarily Provide</h3>
                    <p>When registering for a UBF conference through the App, you may submit the following:</p>

                    <h4>Personal Information</h4>
                    <ul>
                        <li>First name and last name</li>
                        <li>Gender</li>
                    </ul>

                    <h4>Contact Information</h4>
                    <ul>
                        <li>Phone number</li>
                        <li>Email address</li>
                    </ul>

                    <h4>Registration Details</h4>
                    <ul>
                        <li>Category (Student, Recent Graduate, Worker, or Children)</li>
                        <li>Institution / School (Students only)</li>
                        <li>Department (Students only, optional)</li>
                        <li>Marital status</li>
                        <li>Membership status (Member or Guest)</li>
                        <li>UBF Chapter (Members only)</li>
                        <li>Name of person who invited you (Guests only)</li>
                        <li>Date of registration (recorded automatically)</li>
                    </ul>

                    <p>
                        This information is collected solely to process your conference registration and is
                        used only for event coordination by UBF Nigeria.
                    </p>
                </section>

                <hr />

                <section>
                    <h2>3. Information Fetched from Our Servers</h2>
                    <p>
                        The App retrieves the following content from our content management system to display
                        within the App. These are read-only requests — no personal data is sent to the server
                        during this process:
                    </p>
                    <ul>
                        <li>Announcements</li>
                        <li>Sermons and Messages</li>
                        <li>Bible Study materials</li>
                        <li>Conference schedules, rooms, study groups, and hymns</li>
                        <li>Worship hymns and audio resources</li>
                    </ul>
                    <p>
                        Audio files are streamed or downloaded on demand from our audio CDN and cached on
                        your device for offline use.
                    </p>
                </section>

                <hr />

                <section>
                    <h2>4. How We Use Your Information</h2>
                    <div className={styles.table_wrapper}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Purpose</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Favourite &amp; recent hymns</td>
                                    <td>Personalise your in-app experience</td>
                                </tr>
                                <tr>
                                    <td>Font size &amp; theme</td>
                                    <td>Remember your display preferences</td>
                                </tr>
                                <tr>
                                    <td>Cached audio</td>
                                    <td>Enable offline hymn playback</td>
                                </tr>
                                <tr>
                                    <td>Conference registration data</td>
                                    <td>Process and manage event registration</td>
                                </tr>
                                <tr>
                                    <td>CMS content fetches</td>
                                    <td>Display up-to-date announcements, messages, and resources</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p>We do not use your information for advertising, profiling, or sell it to any third party.</p>
                </section>

                <hr />

                <section>
                    <h2>5. Third-Party Services</h2>
                    <p>The App uses the following third-party services:</p>
                    <div className={styles.table_wrapper}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Service</th>
                                    <th>Purpose</th>
                                    <th>Privacy Policy</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Sanity</td>
                                    <td>Content management (announcements, messages, Bible studies, conference content)</td>
                                    <td><a href="https://www.sanity.io/privacy" target="_blank" rel="noreferrer">sanity.io/privacy</a></td>
                                </tr>
                                <tr>
                                    <td>Supabase</td>
                                    <td>Audio file hosting and delivery</td>
                                    <td><a href="https://supabase.com/privacy" target="_blank" rel="noreferrer">supabase.com/privacy</a></td>
                                </tr>
                                <tr>
                                    <td>SheetDB</td>
                                    <td>Conference registration data storage (via Google Sheets)</td>
                                    <td><a href="https://sheetdb.io/privacy-policy" target="_blank" rel="noreferrer">sheetdb.io/privacy-policy</a></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p>
                        Conference registration data submitted through the App is stored in a secured
                        spreadsheet via SheetDB. By submitting a registration, you acknowledge that your
                        data will be processed and stored through this service.
                    </p>
                    <p>
                        These services may log standard network request metadata (e.g. IP address, request
                        timestamp) as part of their normal operations. We do not control this logging. No
                        personally identifiable information is included in content fetch requests.
                    </p>
                </section>

                <hr />

                <section>
                    <h2>6. Data Storage and Security</h2>
                    <ul>
                        <li>All preferences and cached files are stored locally on your device and are removed when you uninstall the App.</li>
                        <li>Conference registration data is transmitted over an encrypted HTTPS connection and stored securely via SheetDB / Google Sheets, accessible only to authorised UBF Nigeria administrators.</li>
                        <li>We implement reasonable technical measures to protect your information, but no method of transmission or storage is 100% secure.</li>
                    </ul>
                </section>

                <hr />

                <section>
                    <h2>7. Children's Privacy</h2>
                    <p>
                        The App is intended for use by university students, staff, and members of UBF Nigeria.
                        We do not knowingly collect personal information from children under the age of 13
                        without parental consent. If you believe a child has submitted personal information through the App, please
                        contact us and we will delete it promptly.
                    </p>
                </section>

                <hr />

                <section>
                    <h2>8. Your Rights</h2>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access the personal information we hold about you</li>
                        <li>Request correction of inaccurate information</li>
                        <li>Request deletion of your conference registration data</li>
                        <li>Withdraw consent at any time for data processing</li>
                    </ul>
                    <p>To exercise any of these rights, please contact us at the address below.</p>
                </section>

                <hr />

                <section>
                    <h2>9. Data Retention</h2>
                    <ul>
                        <li>Local device data is retained until you clear app data or uninstall the App.</li>
                        <li>Conference registration data is retained only as long as necessary for event administration purposes.</li>
                    </ul>
                </section>

                <hr />

                <section>
                    <h2>10. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. Changes will be reflected with
                        an updated effective date. Continued use of the App after changes constitutes
                        acceptance of the revised policy.
                    </p>
                </section>

                <hr />

                <section>
                    <h2>11. Contact Us</h2>
                    <p>
                        If you have questions or concerns about this Privacy Policy or how your data is
                        handled, please contact:
                    </p>
                    <address>
                        <strong>University Bible Fellowship Nigeria</strong><br />
                        Email: <a href="mailto:nigeriaubf@gmail.com">nigeriaubf@gmail.com</a><br />
                        Website: <a href="https://ubf.ng" target="_blank" rel="noreferrer">ubf.ng</a>
                    </address>
                </section>
            </div>
        </div>
    )
}

export default PrivacyPolicyPage
